import React, { useState } from 'react';
import { ProcessoComercial } from '../ComercialDashboard';
import { Button } from '../ui/button';
import { 
  FileText, 
  Edit, 
  Eye, 
  Filter,
  Search,
  Calendar,
  User,
  Home,
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface LocacoesListProps {
  processes: ProcessoComercial[];
  onEdit: (processo: ProcessoComercial) => void;
  onView: (processo: ProcessoComercial) => void;
  onDelete: (processo: ProcessoComercial) => void;
  onRefresh: () => void;
}

export const LocacoesList: React.FC<LocacoesListProps> = ({ 
  processes, 
  onEdit, 
  onView,
  onDelete,
  onRefresh
}) => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'criado':
        return <Clock className="h-4 w-4 text-blue-500" />;
      case 'coletando_dados':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'contrato_rascunho':
        return <FileText className="h-4 w-4 text-purple-500" />;
      case 'revisao':
        return <Edit className="h-4 w-4 text-orange-500" />;
      case 'aprovado':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'finalizado':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'cancelado':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusLabel = (status: string) => {
    const labels = {
      criado: 'Criado',
      coletando_dados: 'Coletando Dados',
      contrato_rascunho: 'Contrato Rascunho',
      revisao: 'Em Revisão',
      aprovado: 'Aprovado',
      finalizado: 'Finalizado',
      cancelado: 'Cancelado'
    };
    return labels[status as keyof typeof labels] || status;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'criado':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'coletando_dados':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'contrato_rascunho':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'revisao':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'aprovado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'finalizado':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelado':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredProcesses = processes.filter(processo => {
    const matchesFilter = filter === 'all' || processo.status === filter;
    const matchesSearch = processo.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         processo.unidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         processo.responsavel?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         processo.responsavel?.last_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getProgressPercent = (status: string) => {
    const statusOrder = ['criado', 'coletando_dados', 'contrato_rascunho', 'revisao', 'aprovado', 'finalizado'];
    const currentIndex = statusOrder.indexOf(status);
    if (status === 'cancelado') return 0;
    return ((currentIndex + 1) / statusOrder.length) * 100;
  };

  // Check if process has a fiador (guarantor)
  const hasGuarantor = (processo: ProcessoComercial) => {
    return processo.partes?.some(parte => parte.tipo_parte === 'fiador') || false;
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex flex-col md:flex-row gap-4 items-center">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Buscar locações..."
              className="pl-10 pr-4 py-2 border border-input rounded-md bg-background text-foreground"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border border-input rounded-md bg-background text-foreground"
          >
            <option value="all">Todos os Status</option>
            <option value="criado">Criado</option>
            <option value="coletando_dados">Coletando Dados</option>
            <option value="contrato_rascunho">Contrato Rascunho</option>
            <option value="revisao">Em Revisão</option>
            <option value="aprovado">Aprovado</option>
            <option value="finalizado">Finalizado</option>
            <option value="cancelado">Cancelado</option>
          </select>
        </div>

        <Button variant="outline" onClick={onRefresh}>
          <Filter className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </div>

      {/* Process List */}
      {filteredProcesses.length === 0 ? (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            Nenhum processo de locação encontrado
          </h3>
          <p className="text-muted-foreground">
            {searchTerm || filter !== 'all' 
              ? 'Tente ajustar os filtros de busca' 
              : 'Clique em "Novo Processo" para criar seu primeiro processo de locação'
            }
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredProcesses.map((processo) => (
            <div key={processo.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-foreground">{processo.titulo}</h3>
                    <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(processo.status)}`}>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(processo.status)}
                        {getStatusLabel(processo.status)}
                      </div>
                    </div>
                    {hasGuarantor(processo) && (
                      <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 border border-blue-200 rounded-full text-xs">
                        <Shield className="h-3 w-3" />
                        Com Fiador
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Home className="h-4 w-4" />
                      {processo.unidade || 'Unidade não informada'}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <User className="h-4 w-4" />
                      {processo.responsavel?.first_name} {processo.responsavel?.last_name || 'Responsável não definido'}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" />
                      {format(new Date(processo.created_at), 'dd/MM/yyyy', { locale: ptBR })}
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-muted-foreground">Progresso</span>
                      <span className="text-xs text-muted-foreground">{Math.round(getProgressPercent(processo.status))}%</span>
                    </div>
                    <div className="w-full bg-secondary rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full transition-all duration-300"
                        style={{ width: `${getProgressPercent(processo.status)}%` }}
                      />
                    </div>
                  </div>

                  {/* Parties and Contracts Info - Enhanced for Rental */}
                  <div className="flex gap-4 text-sm text-muted-foreground">
                    <span>{processo.partes?.length || 0} parte(s)</span>
                    <span>{processo.contratos?.length || 0} contrato(s)</span>
                    {(processo.partes?.filter(p => p.tipo_parte === 'locatario').length || 0) > 0 && (
                      <span>{processo.partes?.filter(p => p.tipo_parte === 'locatario').length} locatário(s)</span>
                    )}
                    {hasGuarantor(processo) && (
                      <span className="text-blue-600">Com garantia</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button variant="outline" size="sm" onClick={() => onView(processo)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => onEdit(processo)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="destructive" size="sm" onClick={() => onDelete(processo)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};